import { yupResolver } from '@hookform/resolvers/yup';
import Modal from '@oncolens/oncolens-ui/lib/components/Common/Modal';
import Button from '@oncolens/oncolens-ui/lib/components/FormControls/Button';
import ButtonWithLoading from '@oncolens/oncolens-ui/lib/components/FormControls/ButtonWithLoading';
import { useHydrateStore } from '@oncolens/oncolens-ui/lib/hooks/common/useHydrateForm';
import { getTestSelector } from '@oncolens/oncolens-ui/lib/utilities/tests';
import { AxiosError } from 'axios';
import clsx from 'clsx';
import { nanoid } from 'nanoid';
import React, { useEffect, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  DraggableProvidedDraggableProps,
  DraggableProvidedDragHandleProps,
  Droppable,
  DropResult
} from 'react-beautiful-dnd';
import {
  Control,
  DeepMap,
  FieldError,
  FieldValues,
  useForm,
  UseFormRegister,
  UseFormWatch
} from 'react-hook-form';
import { FaEllipsisV, FaPencilAlt, FaPlusCircle, FaTrash } from 'react-icons/fa';
import { ImSpinner2 } from 'react-icons/im';
import { UseMutationResult } from 'react-query';
import * as yup from 'yup';

interface DynamicFormDataItem {
	id: string;
	sortOrder?: number;
}

export interface MultiItemDynamicFormProps<T> {
	formOptions: {
		setValue: (field: string, value: any) => void;
		items: T[];
	};
	hydrationOptions: {
		getItemsForHydration: () => Promise<T[]>;
		formKey: string;
		cacheKey: string | string[];
		rehydrateOnSaveIdChange?: boolean;
	};
	shouldUseApiActionOnChange: boolean;
	idForSave: string;
	addItemLabel: string;
	itemNameForFormTitle: string;
	schema: yup.ObjectSchema<any>;
	displayTemplate: (data: T) => JSX.Element;
	formTemplate: (
		control: Control,
		register: UseFormRegister<FieldValues>,
		errors: DeepMap<FieldValues, FieldError>,
		watch: UseFormWatch<FieldValues>
	) => JSX.Element;
	createMutation: UseMutationResult<T[], AxiosError, any>;
	updateMutation: UseMutationResult<void, AxiosError, any>;
	updateSortListMutation?: UseMutationResult<void, AxiosError, any>;
	deleteMutation: UseMutationResult<void, AxiosError, any>;
	wrapperClassName?: string;
	deleteModalHeader: string;
	deleteModalWarning: string;
	testSelector?: string;
}

function MultiItemDynamicForm<T extends DynamicFormDataItem>({
  formOptions: { setValue, items },
  hydrationOptions: { getItemsForHydration, formKey, cacheKey, rehydrateOnSaveIdChange = false },
  shouldUseApiActionOnChange,
  idForSave,
  addItemLabel,
  schema,
  createMutation,
  updateMutation,
  updateSortListMutation,
  deleteMutation,
  displayTemplate,
  formTemplate,
  itemNameForFormTitle,
  wrapperClassName,
  deleteModalHeader,
  deleteModalWarning,
  testSelector = getTestSelector(itemNameForFormTitle, 'multi-item-dynamic-form')
}: MultiItemDynamicFormProps<T>) {
  const [stateItems, setStateItems] = useState(items || []);
  const [activeId, setActiveId] = useState<string | null>(null);
  const { isLoading, setIsHydrated } = useHydrateStore({
    apiCall: async () => getItemsForHydration(),
    keys: { formKey, cacheKey },
    updateStore: setValue,
    shouldRun: Boolean(idForSave) && (!items || (items && items.length === 0))
  });
  const isSortable = Boolean(updateSortListMutation);

  useEffect(() => {
    if (items) {
      setStateItems(items);
    }
  }, [items]);

  useEffect(() => {
    if (rehydrateOnSaveIdChange) {
      setIsHydrated(false);
    }
  }, [idForSave]);

  const addNewItem = () => {
    const newId = nanoid();
    // Unfortunately, there's no way to get typescript to allow us to add an item
    // where the potential values are unknown (such that, a property could be required but we aren't specifying it here)
    // However, its relatively safe to cast to any here because we know this is just the concept of a form item;
    // we make sure not to persist the item to our state of items based on the form schema validation for each item.
    const newItem = {
      id: newId
    } as any;

    if (isSortable) {
      const unsortedItems = [...stateItems, newItem];
      const sortedItems = updateSortOrderProperty(unsortedItems);
      setStateItems(sortedItems);
    } else setStateItems([...stateItems, newItem]);

    setActiveId(newId);
  };

  const persistToForm = (items: T[]) => {
    setValue(formKey, items);
    return items;
  };
  const isEditing = activeId !== null;

  const reorder = (list: T[], startIndex: number, endIndex: number) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  function updateSortOrderProperty(items: T[]) {
    return items.map((item, index) => ({ ...item, sortOrder: index }));
  }

  const updateSortOrderMutation = updateSortListMutation;
  const dragEndHandler = (result: DropResult) => {
    if (!result.destination || result.destination.index === result.source.index) {
      return;
    }

    const items = reorder(stateItems, result.source.index, result.destination.index);

    const copy = updateSortOrderProperty(items);
    setStateItems(copy);

    if (updateSortOrderMutation !== undefined)
      updateSortOrderMutation.mutate({ patientId: idForSave, itemsToUpdate: copy });
  };

  return (
    <div data-cy={testSelector}>
      {isLoading ? (
        <div className="flex items-center justify-center py-4">
          <ImSpinner2 className="text-lg animate-spin" />
        </div>
      ) : (
        <DragDropContext onDragEnd={dragEndHandler}>
          <Droppable droppableId="droppable">
            {(provided, snapshot) => (
              <>
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className={clsx(
                    snapshot.isDraggingOver ? 'bg-blue-200' : '',
                    'transition-colors duration-200 ease-in-out'
                  )}
                >
                  <>
                    {stateItems.map((item, index) => (
                      <Draggable
                        draggableId={item.id}
                        index={item?.sortOrder ?? index}
                        key={item.id}
                      >
                        {(provided, snapshot) => (
                          <Item
                            data={item}
                            createMutation={createMutation}
                            updateMutation={updateMutation}
                            deleteMutation={deleteMutation}
                            shouldUseApiActionOnChange={shouldUseApiActionOnChange}
                            idForSave={idForSave}
                            activeId={activeId}
                            setActive={() => {
                              setActiveId(item.id);
                            }}
                            setInactive={() => {
                              setActiveId(null);
                            }}
                            setStateItems={setStateItems}
                            persistToForm={persistToForm}
                            schema={schema}
                            displayTemplate={displayTemplate}
                            formTemplate={formTemplate}
                            itemNameForFormTitle={itemNameForFormTitle}
                            wrapperClassName={wrapperClassName}
                            deleteModalHeader={deleteModalHeader}
                            deleteModalWarning={deleteModalWarning}
                            dragHandleProps={provided.dragHandleProps as any}
                            dragRef={provided.innerRef as any}
                            draggableProps={provided.draggableProps}
                            isDragging={snapshot.isDragging}
                            isSortable={isSortable}
                          />
                        )}
                      </Draggable>
                    ))}

                    {provided.placeholder}
                  </>
                </div>
                <>
                  {!isEditing && (
                    <button
                      type="button"
                      className="flex items-center justify-center w-full p-8 text-gray-600 transition-colors duration-200 bg-gray-100 border-2 border-gray-300 border-dashed rounded-md hover:bg-white hover:border-gray-400"
                      onClick={addNewItem}
                      data-cy={getTestSelector('add-button', testSelector)}
                    >
                      <FaPlusCircle className="mr-2" /> {addItemLabel}
                    </button>
                  )}
                </>
              </>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </div>
  );
}

interface DynamicFormItemProps<T> {
	data: T;
	createMutation: UseMutationResult<T[], AxiosError, any>;
	updateMutation: UseMutationResult<void, AxiosError, any>;
	deleteMutation: UseMutationResult<void, AxiosError, any>;
	shouldUseApiActionOnChange: boolean;
	idForSave: string;
	activeId: string | null;
	setActive: () => void;
	setInactive: () => void;
	setStateItems: React.Dispatch<React.SetStateAction<T[]>>;
	persistToForm: (items: T[]) => T[];
	schema: yup.ObjectSchema<any>;
	itemNameForFormTitle: string;
	displayTemplate: (data: T) => JSX.Element;
	formTemplate: (
		control: Control,
		register: UseFormRegister<FieldValues>,
		errors: DeepMap<FieldValues, FieldError>,
		watch: UseFormWatch<FieldValues>
	) => JSX.Element;
	wrapperClassName?: string;
	deleteModalHeader: string;
	deleteModalWarning: string;
	dragHandleProps: DraggableProvidedDragHandleProps | undefined;
	dragRef: (element?: HTMLElement | null | undefined) => any;
	draggableProps: DraggableProvidedDraggableProps;
	isDragging: boolean;
	isSortable: boolean;
}

function Item<T extends DynamicFormDataItem>({
  data,
  createMutation,
  updateMutation,
  deleteMutation,
  shouldUseApiActionOnChange,
  idForSave,
  activeId,
  setActive,
  setInactive,
  setStateItems,
  persistToForm,
  schema,
  displayTemplate,
  formTemplate,
  itemNameForFormTitle,
  wrapperClassName,
  deleteModalHeader,
  deleteModalWarning,
  dragHandleProps,
  dragRef,
  draggableProps,
  isDragging,
  isSortable
}: DynamicFormItemProps<T>) {
  const isEditing = data.id === activeId;
  const [isApiActionExecuting, setApiActionExecuting] = useState(false);
  const isPreviouslySaved = React.useMemo(() => schema.isValidSync(data), [data]);

  const handleApiAction = (
    apiAction: () => Promise<T | T[] | void>,
    updateStateAction: (result: T | T[] | void) => void
  ) => {
    setApiActionExecuting(true);

    apiAction()
      .then((result) => {
        updateStateAction(result);
        setApiActionExecuting(false);
      })
      .catch(() => {
        setApiActionExecuting(false);
      });
  };

  const handleApiUpdate = (formValues: T) => {
    handleApiAction(
      async () =>
        updateMutation.mutateAsync({
          id: data.id,
          data: formValues
        }),
      () => {
        handleUpdate(formValues);
      }
    );
  };

  const handleApiDelete = () => {
    handleApiAction(
      async () => deleteMutation.mutateAsync(data.id),
      () => {
        handleRemove();
      }
    );
  };

  const handleApiCreate = (formValues: T) => {
    handleApiAction(
      async () =>
        createMutation.mutateAsync({
          id: idForSave,
          data: formValues
        }),
      (data) => {
        if (data) {
          handleUpdate(Array.isArray(data) ? data[0] : data);
        }
      }
    );
  };

  // Handles the state update only - this occurs separately of updates that call the API
  const handleUpdate = (formValues: T) => {
    setStateItems((items) =>
      persistToForm(items.map((item) => (item.id === data.id ? { ...item, ...formValues } : item)))
    );

    setInactive();
  };

  // Cancel occurs instead of remove when we already 'saved' the item
  // and are now updating it (vs the form showing up when adding a new panel)
  const handleCancel = () => {
    if (!isPreviouslySaved) {
      handleRemove();
    } else {
      setInactive();
    }
  };

  const handleRemove = () => {
    setStateItems((items) => persistToForm(items.filter((item) => item.id !== data.id)));

    setInactive();
  };

  return (
    <div
      {...draggableProps}
      ref={dragRef}
      className={clsx(
        isDragging ? 'bg-gray-200 ' : 'bg-white',
        'relative mb-3 border border-gray-300 rounded-md shadow-md transition-colors duration-200 ease-in-out'
      )}
    >
      {isEditing ? (
        <ItemForm
          data={data}
          shouldUseApiActionOnChange={shouldUseApiActionOnChange}
          isPreviouslySaved={isPreviouslySaved}
          isApiActionExecuting={isApiActionExecuting}
          onCancel={handleCancel}
          onUpdate={handleUpdate}
          onApiCreate={handleApiCreate}
          onApiUpdate={handleApiUpdate}
          template={formTemplate}
          schema={schema}
          itemNameForFormTitle={itemNameForFormTitle}
          wrapperClassName={wrapperClassName}
        />
      ) : (
        <ItemDisplay
          data={data}
          shouldUseApiActionOnChange={shouldUseApiActionOnChange}
          onDelete={handleApiDelete}
          onRemove={handleRemove}
          onSetActive={setActive}
          template={displayTemplate}
          deleteModalHeader={deleteModalHeader}
          deleteModalWarning={deleteModalWarning}
          dragHandleProps={dragHandleProps}
          isSortable={isSortable}
        />
      )}
      <div
        className={clsx(
          'absolute top-0 left-0 w-full h-full bg-gray-300 opacity-0 pointer-events-none transition-opacity duration-300',
          Boolean(activeId) && !isEditing && 'opacity-50 cursor-not-allowed pointer-events-auto'
        )}
      ></div>
    </div>
  );
}

interface ItemFormProps<T> {
	data: T;
	shouldUseApiActionOnChange: boolean;
	isPreviouslySaved: boolean;
	isApiActionExecuting: boolean;
	onCancel: () => void;
	onUpdate: (formValues: T) => void;
	onApiCreate: (formValues: T) => void;
	onApiUpdate: (formValues: T) => void;
	schema: yup.ObjectSchema<any>;
	template: (
		control: Control,
		register: UseFormRegister<FieldValues>,
		errors: DeepMap<FieldValues, FieldError>,
		watch: UseFormWatch<FieldValues>
	) => JSX.Element;
	itemNameForFormTitle: string;
	wrapperClassName?: string;
}

function ItemForm<T>({
  data,
  shouldUseApiActionOnChange,
  isPreviouslySaved,
  isApiActionExecuting,
  onCancel,
  onUpdate,
  onApiCreate,
  onApiUpdate,
  schema,
  template,
  itemNameForFormTitle,
  wrapperClassName
}: ItemFormProps<T>) {
  const {
    control,
    register,
    reset,
    getValues,
    watch,
    trigger,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    reset(data as any);
  }, []);

  const validateCreateUpdate = (createUpdateAction: () => void) => {
    trigger()
      .then((isValid) => {
        if (isValid) {
          createUpdateAction();
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleCancel = () => {
    onCancel();
    reset(data as any);
  };

  const handleUpdate = () => {
    validateCreateUpdate(() => {
      onUpdate(getValues() as T);
    });
  };

  const handleApiUpdate = () => {
    validateCreateUpdate(() => {
      onApiUpdate(getValues() as T);
    });
  };

  const handleApiCreate = () => {
    validateCreateUpdate(() => {
      onApiCreate(getValues() as T);
    });
  };

  return (
    <div className={clsx(wrapperClassName ?? 'grid grid-cols-5 gap-3 p-3')}>
      <div className="col-span-full">
        <h2 className="pb-0.5 pr-1 inline-block text-base font-semibold border-b-2 border-oncolensBlue-400">
          {`${isPreviouslySaved ? 'Update' : 'Add'} ${itemNameForFormTitle}`}
        </h2>
      </div>
      {template(control, register, errors, watch)}
      <div className="flex my-1 col-span-full">
        <div className="flex-1 block mr-2 sm:inline-block sm:ml-auto sm:flex-initial">
          <Button
            buttonStyle="secondary"
            buttonColor="blue"
            onClick={handleCancel}
            type="button"
            className="inline-block rounded sm:py-1"
          >
						Cancel
          </Button>
        </div>
        <div className="flex-1 block sm:inline-block sm:flex-initial">
          {shouldUseApiActionOnChange ? (
            <ButtonWithLoading
              buttonStyle="primary"
              onClick={isPreviouslySaved ? handleApiUpdate : handleApiCreate}
              loading={isApiActionExecuting}
              buttonColor="blue"
              className="inline-block rounded sm:px-4 sm:py-1"
            >
              {isPreviouslySaved ? 'Update' : 'Add'}
            </ButtonWithLoading>
          ) : (
            <Button
              buttonStyle="primary"
              buttonColor="blue"
              onClick={handleUpdate}
              className="inline-block rounded sm:px-4 sm:py-1"
            >
              {isPreviouslySaved ? 'Update' : 'Add'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

interface ItemDisplayProps<T> {
	data: T;
	shouldUseApiActionOnChange: boolean;
	onDelete: () => void;
	onRemove: () => void;
	onSetActive: () => void;
	template: (data: T) => JSX.Element;
	deleteModalHeader: string;
	deleteModalWarning: string;
	dragHandleProps: DraggableProvidedDragHandleProps | undefined;
	isSortable: boolean;
}

function ItemDisplay<T>({
  data,
  shouldUseApiActionOnChange,
  onDelete,
  onRemove,
  onSetActive,
  template,
  deleteModalHeader,
  deleteModalWarning,
  dragHandleProps,
  isSortable
}: ItemDisplayProps<T>) {
  return (
    <div className="flex items-center justify-center p-3 pb-4" data-cy="item-display">
      {template(data)}
      <div className="flex ml-auto space-x-2">
        <FaPencilAlt
          className="box-content p-2 mr-px text-gray-600 transition-colors duration-200 cursor-pointer hover:text-gray-800"
          onClick={onSetActive}
          data-cy="item-display-edit-button"
        />
        {shouldUseApiActionOnChange ? (
          <Modal
            triggerElement={
              <FaTrash
                className="box-content p-2 text-gray-600 transition-colors duration-200 cursor-pointer hover:text-gray-800"
                data-cy="item-display-delete-button"
              />
            }
          >
            <Modal.Header title={deleteModalHeader} showClose={true} />
            <Modal.Content>
              <p>{deleteModalWarning}</p>
            </Modal.Content>
            <Modal.Buttons
              buttons={[
                {
                  label: 'Cancel',
                  style: 'secondary',
                  color: 'gray',
                  closeAfterClick: true,
                  className: 'mr-2'
                },
                {
                  label: 'Delete',
                  style: 'primary',
                  color: 'red',
                  closeAfterClick: true,
                  onClick: () => {
                    setTimeout(onDelete, 300);
                  }
                }
              ]}
            />
            {/* TODO: Remove set timeout. right now, the modal needs to live outside the removed part of the component but also still have a trigger in the right place
                  which means that clicking 'Delete' removes the whole modal tree before the close animation can finish. */}
          </Modal>
        ) : (
          <FaTrash
            className="box-content p-2 text-gray-600 transition-colors duration-200 cursor-pointer hover:text-gray-800"
            onClick={onRemove}
            data-cy="item-display-delete-button"
          />
        )}
        {isSortable && (
          <div
            {...dragHandleProps}
            className="box-content flex items-center -space-x-2 text-gray-600 transition-colors duration-200 cursor-pointer hover:text-gray-800"
          >
            <FaEllipsisV className="text-current" />
            <FaEllipsisV className="text-current" />
          </div>
        )}
      </div>
    </div>
  );
}

export { MultiItemDynamicForm as default };
